import { RetweetOutlined } from '@ant-design/icons';
import AppIconButton from '@nxsys/components/AppIconButton';
import {
  IconFileListCheck,
  IconFileListTimesOutline,
} from '@nxsys/components/AppIconSvg';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import {
  defaultPaymentFrequency,
  PAYMENT_MODEL_ENUM,
  paymentModels,
} from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Payroll } from '@nxsys/store/slices/revenueFilings/types';
import { Icons } from '@nxsys/types/icons';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { P30P32RecordType } from '@nxsys/types/models/hmrcReports';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import DraftPayrollActionCell from './DraftPayrollActionCell';
import { verifierColumn } from './verifierColumn';
import {
  formatFullName,
  formatStatus,
  getAppConfig,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { formatMoney, formatMoneyExport } from '@nxsys/utils/number-helper';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { find, isEmpty, some } from 'lodash';
import moment from 'moment';

import {
  getAllSharedColumns,
  getConfirmedPayrollColumns,
  taxPeriodColumn,
} from './payeUmbrellaPayrollColumns';
import {
  CISColumnParams,
  cisTaxPeriodColumn,
  cisAgencyNameColumn,
  cisNameColumn,
  cisPaymentCompanyColumn,
  getCISIdentityColumns,
  getCISFinancialColumns,
  getCISDeductionColumns,
  getCISCommonColumns,
  getSelfBillColumns,
  selfBillRefColumn,
  selfBillDateColumn,
  invoiceNoConfirmedColumn,
} from './cisPayrollColumns';

/**
 * SafeRec / VeriPaye are configured per company, and a payment summary listing
 * spans several companies, so the integration is read off each payroll's own
 * companyRef rather than off the logged-in tenant.
 */
export const hasSafeRecSetting = (payroll?: Payroll | null): boolean =>
  !!(payroll as any)?.companyRef?.safeRecSetting?.integratedCompanyId;

export const hasVeriPayeSetting = (payroll?: Payroll | null): boolean =>
  !!(payroll as any)?.companyRef?.veriPayeSettings?.id;

export const getDraftPayrollCISColumns = ({
  messages,
  payrollCountry,
  handleViewDraftPayslipById,
  handleConfirmDraftPayroll,
  handleDeleteDraftPayroll,
  handleSendDraftPayslip,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleViewDraftPayslipById: (id: string) => void;
  handleConfirmDraftPayroll: (id: string) => void;
  handleDeleteDraftPayroll: (id: string) => void;
  handleSendDraftPayslip?: (record: DraftPayroll) => void;
  isBureau?: boolean;
}): ColumnsType<DraftPayroll> => {
  const p: CISColumnParams = { messages, payrollCountry, isBureau };

  return [
    {
      key: 'actions',
      title: messages['common.action'],
      align: 'left',

      render: (record) => {
        return (
          <DraftPayrollActionCell
            record={record}
            onDownload={handleViewDraftPayslipById}
            onConfirm={handleConfirmDraftPayroll}
            onDelete={handleDeleteDraftPayroll}
            onSendEmail={handleSendDraftPayslip}
          />
        );
      },
      width: 200,
      fixed: 'left',
    },
    ...getCISIdentityColumns(p),
    ...getCISFinancialColumns(p),
    ...getCISDeductionColumns(p),
    ...getCISCommonColumns(p),
    ...getSelfBillColumns(p),
  ];
};

type DraftPayrollColumnsParams = {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleViewDraftPayslipById: (id: string) => void;
  handleConfirmDraftPayroll: (id: string) => void;
  handleDeleteDraftPayroll: (id: string) => void;
  handleSendDraftPayslip?: (record: DraftPayroll) => void;
  handleViewVerdict?: (record: DraftPayroll) => void;
  isBureau?: boolean;
};

const noop = () => undefined;

// Payroll table columns Draft Payroll does not show. The payroll's own ID and
// sales invoice ref only exist once confirmed; the rest are columns each
// system/country's draft table never had, kept out as before.
const getDraftHiddenColumnKeys = (
  isIreland: boolean,
  isBureau?: boolean,
): string[] => [
  'actions',
  'id',
  'salesInvRef',
  ...(isIreland
    ? isBureau
      ? ['employeeId', 'paymentModel', 'holidayPay', 'holidayPayRetained', 'holidayPaid', 'paid', 'outstanding']
      : ['hpEmployerCosts', 'hpEmployerCostsPaid']
    : isBureau
      ? ['employeeId', 'paymentModel', 'sncp', 'appLevy', 'totalPayment', 'totalDeduction', 'paid', 'outstanding']
      : ['department']),
];

// Draft Payroll shows the Payroll table's columns — same names, same order —
// so a payroll looks the same before and after it is confirmed, and so do the
// two CSV exports, which are built from these columns (NX-5791). The Payroll
// table may have more columns (getDraftHiddenColumnKeys, SafeRec, VeriPaye).
// The action cell is the draft's own.
const getDraftPayrollTableColumns = ({
  messages,
  payrollCountry,
  handleViewDraftPayslipById,
  handleConfirmDraftPayroll,
  handleDeleteDraftPayroll,
  handleSendDraftPayslip,
  handleViewVerdict,
  isBureau,
}: DraftPayrollColumnsParams): ColumnsType<DraftPayroll> => {
  const actionsColumn: ColumnsType<DraftPayroll>[number] = {
    key: 'actions',
    title: messages['common.action'],
    align: 'left',
    render: (record) => (
      <DraftPayrollActionCell
        record={record}
        onDownload={handleViewDraftPayslipById}
        onConfirm={handleConfirmDraftPayroll}
        onDelete={handleDeleteDraftPayroll}
        onSendEmail={handleSendDraftPayslip}
      />
    ),
    width: 200,
    fixed: 'left',
  };

  const payrollColumns = getPayrollPayeUmbrellaColumns({
    messages,
    payrollCountry,
    isBureau,
    handleViewPayslipById: noop,
    handleSendPayslip: noop,
    handleSafeRecPayroll: noop,
    handleOpenReverse: noop,
    handleView: noop,
    hideReverse: true,
  }) as ColumnsType<DraftPayroll>;
  const hiddenKeys = getDraftHiddenColumnKeys(
    payrollCountry === IRELAND,
    isBureau,
  );

  return [
    actionsColumn,
    ...(handleViewVerdict ? [verifierColumn(messages, handleViewVerdict)] : []),
    ...payrollColumns.filter(
      (column) => !hiddenKeys.includes(String(column.key)),
    ),
  ];
};

export const getDraftPayrollPayeUmbrellaColumns = (
  params: DraftPayrollColumnsParams,
): ColumnsType<DraftPayroll> => getDraftPayrollTableColumns(params);

export const getDraftPayrollBureauColumns = (
  params: Omit<DraftPayrollColumnsParams, 'isBureau'>,
): ColumnsType<DraftPayroll> =>
  getDraftPayrollTableColumns({ ...params, isBureau: true });

export const getPayrollCISColumns = ({
  messages,
  payrollCountry,
  handleViewPayslipById,
  handleSendPayslip,
  handleSafeRecPayroll,
  handleViewSafeRecSubmission,
  handleOpenReverse,
  handleView,
  isBureau,
  hideReverse = false,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleViewPayslipById: (id: string) => void;
  handleSendPayslip: (data: Payroll) => void;
  handleSafeRecPayroll: (data: Payroll) => void;
  handleViewSafeRecSubmission?: (data: Payroll) => void;
  handleOpenReverse: (data: Payroll[]) => void;
  handleView: (id: string) => void;
  isBureau?: boolean;
  hideReverse?: boolean;
}): ColumnsType<Payroll> => {
  const p: CISColumnParams = { messages, payrollCountry, isBureau };
  return [
    {
      key: 'actions',
      title: messages['common.action'],
      align: 'left',
      render: (record) => {
        const { selfBillRef, isSendingEmailSelfBill } = record;
        const latestEmailLog = !isEmpty(selfBillRef?.emailLogs)
          ? selfBillRef.emailLogs?.reduce((latest: any, log: any) => {
              return new Date(log.createdAt) > new Date(latest.createdAt)
                ? log
                : latest;
            }, selfBillRef.emailLogs[0])
          : null;
        const isSentEmailSelfBillSuccess =
          !selfBillRef?.isSendEmailFailed &&
          !selfBillRef?.errorSendEmailFailed &&
          (selfBillRef?.isSendEmailSelfBill ||
            latestEmailLog?.status === 'SUCCESS');
        const isSentEmailSelfBillFailed =
          selfBillRef?.isSendEmailFailed && selfBillRef?.errorSendEmailFailed;
        const isSendSafeRecSuccess = record?.isSendSafeRecSuccess;
        const isSendSafeRecFailed =
          !record?.isSendSafeRecSuccess && record?.safeRecErrorMsg;
        return (
          <ul className="flex items-center gap-2">
            <li>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Tooltip title={messages['common.view']}>
                  <span
                    className="cursor-pointer"
                    onClick={() => handleView(record.id)}
                  >
                    <Icons.IconEye />
                  </span>
                </Tooltip>
                {selfBillRef && (
                  <Tooltip title={messages['common.download']}>
                    <span
                      className="cursor-pointer"
                      onClick={() =>
                        handleViewPayslipById(record.selfBillRef?.id)
                      }
                    >
                      <Icons.IconDownload />
                    </span>
                  </Tooltip>
                )}
                {!hideReverse && (
                  <Tooltip title={messages['timesheet.reverse']}>
                    <span
                      className="cursor-pointer"
                      onClick={() => handleOpenReverse([record])}
                    >
                      <RetweetOutlined style={{ color: '#ff9900' }} />
                    </span>
                  </Tooltip>
                )}
              </div>
            </li>
            {selfBillRef && (
              <>
                <li>
                  <AppIconButton
                    title={
                      isSendingEmailSelfBill
                        ? messages['common.sending']
                        : isSentEmailSelfBillSuccess
                          ? messages['common.emailSentPayslipSuccess']
                          : isSentEmailSelfBillFailed
                            ? selfBillRef?.errorSendEmailFailed ||
                              messages['common.emailSentPayslipFailed']
                            : messages['common.sendEmailPayslip']
                    }
                    icon={
                      isSentEmailSelfBillFailed ? (
                        <Icons.IconSendEmailFailed />
                      ) : (
                        <Icons.IconEmail
                          color={
                            isSendingEmailSelfBill
                              ? '#ff9900'
                              : isSentEmailSelfBillSuccess
                                ? defaultTheme.theme.palette.green[500]
                                : '#666666'
                          }
                        />
                      )
                    }
                    onClick={() => handleSendPayslip(record)}
                  />
                </li>
                {!isBureau && hasSafeRecSetting(record) && (
                  <li>
                    <AppIconButton
                      title={
                        isSendSafeRecFailed
                          ? record?.safeRecErrorMsg
                          : isSendSafeRecSuccess
                            ? messages['common.sentToSafeRecSuccess']
                            : messages['common.sendToSafeRec']
                      }
                      icon={
                        isSendSafeRecFailed ? (
                          <Icons.IconShield1 />
                        ) : (
                          <Icons.IconShield
                            color={
                              isSendSafeRecSuccess
                                ? defaultTheme.theme.palette.green[500]
                                : '#666666'
                            }
                          />
                        )
                      }
                      onClick={() => handleSafeRecPayroll(record)}
                    />
                  </li>
                )}
                {!isBureau &&
                  hasSafeRecSetting(record) &&
                  handleViewSafeRecSubmission &&
                  (isSendSafeRecSuccess || isSendSafeRecFailed) && (
                    <li>
                      <AppIconButton
                        title={
                          messages['common.viewSafeRecSubmission'] ||
                          'View SafeRec Submission'
                        }
                        icon={
                          isSendSafeRecSuccess ? (
                            <IconFileListCheck
                              color={defaultTheme.theme.palette.green[500]}
                            />
                          ) : (
                            <IconFileListTimesOutline color="#F04438" />
                          )
                        }
                        onClick={() => handleViewSafeRecSubmission(record)}
                      />
                    </li>
                  )}
              </>
            )}
            {record?.isPAL && (
              <li>
                <Tag color="warning" className="ml-1">
                  PAL
                </Tag>
              </li>
            )}
          </ul>
        );
      },
      width: 200,
      fixed: 'left',
    },
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
    },
    {
      key: 'timeSheetRef',
      title: messages['table.timesheetId'],
      dataIndex: 'timeSheetRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => (
        <span>
          {value?.id || '_'}
        </span>
      ),
    },
    {
      key: 'subcontractorRef',
      title: messages['common.employeeId'],
      dataIndex: 'subcontractorRef',
      render: (value) => value?.id || '_',
    },
    {
      key: 'subcontractorRef',
      title: isBureau ? messages['table.employerRef'] : messages['common.agencyRef'],
      dataIndex: 'subcontractorRef',
      render: (value) =>
        `${value?.departmentPrefix || value?.primaryAgency?.agencyPrefix || '___'}/${
          value?.agencyRef || value?.id || '_'
        }`,
    },
    cisAgencyNameColumn(p),
    cisPaymentCompanyColumn(p),
    cisNameColumn(p),
    // Tax Period sits right after the name column so it stays visible without
    // horizontal scrolling, matching the legacy report layout.
    cisTaxPeriodColumn(p),
    ...getCISFinancialColumns(p),
    ...getCISDeductionColumns(p),
    ...getCISCommonColumns(p),
    selfBillRefColumn(p),
    selfBillDateColumn(p),
    invoiceNoConfirmedColumn(p),
  ] as ColumnsType<Payroll>;
};

export const getPayrollPayeUmbrellaColumns = ({
  messages,
  payrollCountry,
  handleViewPayslipById,
  handleSendPayslip,
  handleSafeRecPayroll,
  handleViewSafeRecSubmission,
  handleOpenReverse,
  handleView,
  isBureau,
  hideReverse = false,
  showSafeRec = false,
  showVeriPaye = false,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleViewPayslipById: (id: string) => void;
  handleSendPayslip: (data: Payroll) => void;
  handleSafeRecPayroll: (data: Payroll) => void;
  handleViewSafeRecSubmission?: (data: Payroll) => void;
  handleOpenReverse: (data: Payroll[]) => void;
  handleView: (id: string) => void;
  isBureau?: boolean;
  hideReverse?: boolean;
  /** Company has SafeRec set up — companyRef.safeRecSetting.integratedCompanyId. */
  showSafeRec?: boolean;
  /** Company has VeriPaye set up — companyRef.veriPayeSettings.id. */
  showVeriPaye?: boolean;
}): ColumnsType<Payroll> => {
  const defaultPaymentModel = getAppConfig('paymentModels');
  const isIreland = payrollCountry === IRELAND;

  const actionsColumn: ColumnsType<Payroll>[number] = {
    key: 'actions',
    title: messages['common.action'],
    align: 'left',
    render: (record) => {
      const { selfBillRef, isSendingEmailSelfBill } = record;
      const latestEmailLog = !isEmpty(selfBillRef?.emailLogs)
        ? selfBillRef.emailLogs?.reduce((latest: any, log: any) => {
            return new Date(log.createdAt) > new Date(latest.createdAt)
              ? log
              : latest;
          }, selfBillRef.emailLogs[0])
        : null;
      const isSentEmailSelfBillSuccess =
        !selfBillRef?.isSendEmailFailed &&
        !selfBillRef?.errorSendEmailFailed &&
        (selfBillRef?.isSendEmailSelfBill ||
          latestEmailLog?.status === 'SUCCESS');
      const isSentEmailSelfBillFailed =
        selfBillRef?.isSendEmailFailed && selfBillRef?.errorSendEmailFailed;

      const isSendSafeRecSuccess = record?.isSendSafeRecSuccess;
      const isSendSafeRecFailed =
        !record?.isSendSafeRecSuccess && record?.safeRecErrorMsg;

      return (
        <ul className="flex items-center justify-center gap-2">
          <li>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 8,
                alignItems: 'center',
              }}
            >
              <Tooltip title={messages['common.view']}>
                <span
                  className="cursor-pointer"
                  onClick={() => handleView(record.id)}
                >
                  <Icons.IconEye />
                </span>
              </Tooltip>
              {selfBillRef && (
                <Tooltip title={messages['common.download']}>
                  <span
                    className="cursor-pointer"
                    onClick={() =>
                      handleViewPayslipById(record.selfBillRef?.id)
                    }
                  >
                    <Icons.IconDownload />
                  </span>
                </Tooltip>
              )}
              {!hideReverse && (
                <Tooltip title={messages['timesheet.reverse']}>
                  <span
                    className="cursor-pointer"
                    onClick={() => handleOpenReverse([record])}
                  >
                    <RetweetOutlined style={{ color: '#ff9900' }} />
                  </span>
                </Tooltip>
              )}
            </div>
          </li>
          {selfBillRef && (
            <>
              <li>
                <AppIconButton
                  title={
                    isSendingEmailSelfBill
                      ? messages['common.sending']
                      : isSentEmailSelfBillSuccess
                        ? messages['common.emailSentPayslipSuccess']
                        : isSentEmailSelfBillFailed
                          ? selfBillRef?.errorSendEmailFailed ||
                            messages['common.emailSentPayslipFailed']
                          : messages['common.sendEmailPayslip']
                  }
                  icon={
                    isSentEmailSelfBillFailed ? (
                      <Icons.IconSendEmailFailed />
                    ) : (
                      <Icons.IconEmail
                        color={
                          isSendingEmailSelfBill
                            ? '#ff9900'
                            : isSentEmailSelfBillSuccess
                              ? defaultTheme.theme.palette.green[500]
                              : '#666666'
                        }
                      />
                    )
                  }
                  onClick={() => handleSendPayslip(record)}
                />
              </li>
              {!isBureau && hasSafeRecSetting(record) && (
                <li>
                  <AppIconButton
                    title={
                      isSendSafeRecFailed
                        ? record?.safeRecErrorMsg
                        : isSendSafeRecSuccess
                          ? messages['common.sentToSafeRecSuccess']
                          : messages['common.sendToSafeRec']
                    }
                    icon={
                      isSendSafeRecFailed ? (
                        <Icons.IconShield1 />
                      ) : (
                        <Icons.IconShield
                          color={
                            isSendSafeRecSuccess
                              ? defaultTheme.theme.palette.green[500]
                              : '#666666'
                          }
                        />
                      )
                    }
                    onClick={() => handleSafeRecPayroll(record)}
                  />
                </li>
              )}
              {!isBureau &&
                hasSafeRecSetting(record) &&
                handleViewSafeRecSubmission &&
                (isSendSafeRecSuccess || isSendSafeRecFailed) && (
                  <li>
                    <AppIconButton
                      title={
                        messages['common.viewSafeRecSubmission'] ||
                        'View SafeRec Submission'
                      }
                      icon={
                        isSendSafeRecSuccess ? (
                          <IconFileListCheck
                            color={defaultTheme.theme.palette.green[500]}
                          />
                        ) : (
                          <IconFileListTimesOutline color="#F04438" />
                        )
                      }
                      onClick={() => handleViewSafeRecSubmission(record)}
                    />
                  </li>
                )}
            </>
          )}
          {record?.isPAL && (
            <li>
              <Tag color="warning" className="ml-1">
                PAL
              </Tag>
            </li>
          )}
        </ul>
      );
    },
    width: 200,
    fixed: 'left',
  };

  const p = { messages, payrollCountry, isBureau };

  // Identity columns shared between Ireland and UK paths
  const identityColumns: ColumnsType<Payroll> = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 200,
    },
    {
      key: 'paymentCompany',
      title: isIreland
        ? messages['common.employerId']
        : messages['common.companyId'],
      dataIndex: 'companyRef',
      width: 200,
      render: (value) => <span>{value?.id || '_'}</span>,
    },
    {
      key: 'employeeId',
      title: messages['common.employeeId'],
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => value?.id || '_',
    },
    {
      key: 'subcontractorRef',
      title:
        isBureau || isIreland
          ? messages['table.employerRef']
          : messages['common.agencyRef'],
      dataIndex: 'subcontractorRef',
      width: 250,
      render: (value) =>
        `${value?.departmentPrefix || value?.primaryAgency?.agencyPrefix || '___'}/${
          value?.agencyRef || value?.id || '_'
        }`,
    },
    {
      key: 'forename',
      title: messages['common.forename'],
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => value?.forename || '_',
    },
    {
      key: 'surname',
      title: messages['common.surname'],
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => value?.surname || '_',
    },
    // Tax Period sits right after the name columns so it stays visible without
    // horizontal scrolling, matching the legacy report layout.
    taxPeriodColumn(p) as ColumnsType<Payroll>[number],
    {
      key: 'taxYear',
      title: messages['common.taxYear'],
      width: 150,
      align: 'right',
      render: (record) =>
        record?.taxYear || record?.selfBillRef?.taxYear || '_',
    },
    {
      key: 'jobDescription',
      title: messages['common.jobTitle'],
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (value) => value?.jobDescription || '_',
    },
    {
      key: 'paymentModel',
      title: messages['common.paymentModel'],
      width: 200,
      render: (value) =>
        paymentModels[value?.paymentModel as keyof typeof paymentModels] || 'Not Set',
    },
    {
      key: 'paymentCompany',
      title: isBureau ? messages['common.employer'] : messages['common.paymentCompany'],
      dataIndex: 'companyRef',
      width: 350,
      render: (value) => (
        <span>{value?.companyName || '_'}</span>
      ),
    },
  ];

  if (isIreland) {
    return [
      actionsColumn,
      ...identityColumns,
      ...(isBureau
        ? []
        : [
            {
              key: 'agencyName',
              title: messages['common.agencyName'],
              dataIndex: 'agencyName',
              width: 350,
              sorter: true,
              sortDirections: ['descend', 'ascend'],
            },
          ] as ColumnsType<Payroll>),
      ...(getAllSharedColumns(p) as ColumnsType<Payroll>),
      ...(getConfirmedPayrollColumns(p) as ColumnsType<Payroll>),
    ];
  }

  return [
    actionsColumn,
    ...identityColumns,
    ...(isBureau
      ? []
      : [
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            dataIndex: 'agencyName',
            width: 350,
            sorter: true,
            sortDirections: ['descend', 'ascend'],
          },
        ] as ColumnsType<Payroll>),
    ...(getAllSharedColumns(p) as ColumnsType<Payroll>),
    ...(getConfirmedPayrollColumns(p) as ColumnsType<Payroll>),
    // Umbrella-only: PAYE_FTH, SafeRec, VeriPaye. Employer AVC is the AVC
    // Employer column already in getAllSharedColumns, so it is not repeated.
    ...(!isBureau ? [
      ...((some(
        defaultPaymentModel,
        (pm) => pm.value === PAYMENT_MODEL_ENUM.PAYE_FTH,
      )
        ? [
            {
              key: 'payeeUmbrellaValue',
              title: messages['common.takeHomePercent'],
              align: 'right' as const,
              dataIndex: 'payeeUmbrellaValue',
              width: 170,
              render: (props: any) =>
                formatMoney(
                  props?.takeHomePayPercent || 0,
                  showCurrencyByCountry(payrollCountry),
                ),
            },
            {
              key: 'payeeUmbrellaValue',
              title: messages['common.umbrellaAdvSelfEmployedPmt'],
              align: 'right' as const,
              dataIndex: 'payeeUmbrellaValue',
              width: 300,
              render: (props: any) =>
                formatMoney(
                  props?.selfEmployedIncome || 0,
                  showCurrencyByCountry(payrollCountry),
                ),
            },
            {
              key: 'payeeUmbrellaValue',
              title: messages['common.additionalMargin'],
              align: 'right' as const,
              dataIndex: 'payeeUmbrellaValue',
              render: (props: any) =>
                formatMoney(
                  props?.additionalMargin || 0,
                  showCurrencyByCountry(payrollCountry),
                ),
              width: 170,
            },
          ]
        : []) as ColumnsType<Payroll>),
      // SafeRec / VeriPaye only mean something once the company is integrated.
      // The column itself is dropped when no row's company has the integration,
      // and within a column each cell follows its own row's companyRef — one
      // listing spans several companies, only some of which are set up.
      ...((showSafeRec
        ? [
            {
              key: 'safeRecStatus',
              title: messages['common.safeRecStatus'],
              align: 'center' as const,
              width: 150,
              render: (record: Payroll) => {
                if (!hasSafeRecSetting(record)) return '_';
                const status = record?.isSendSafeRecSuccess
                  ? 'SUCCESS'
                  : record?.safeRecErrorMsg
                    ? 'FAILED'
                    : 'NOT_SENT';
                return (
                  <AppTagStatus className={formatStatus(status)}>
                    {formatStatus(status)}
                  </AppTagStatus>
                );
              },
            },
            {
              key: 'safeRecErrorMsg',
              title: messages['common.safeRecErrorMsg'],
              width: 200,
              render: (record: Payroll) =>
                hasSafeRecSetting(record) ? record?.safeRecErrorMsg || '_' : '_',
            },
          ]
        : []) as ColumnsType<Payroll>),
      ...((showVeriPaye
        ? [
            {
              key: 'veriPayeStatus',
              title: messages['common.veriPayeStatus'],
              align: 'center' as const,
              width: 150,
              render: (record: Payroll) => {
                if (!hasVeriPayeSetting(record)) return '_';
                const status =
                  (record as any)?.veriPayeSubmission?.status || 'NOT_SENT';
                return (
                  <AppTagStatus className={formatStatus(status)}>
                    {formatStatus(status)}
                  </AppTagStatus>
                );
              },
            },
          ]
        : []) as ColumnsType<Payroll>),
    ] as ColumnsType<Payroll> : []),
  ];
};

export const getHolidayPayBalanceDetailColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<IHolidayPayBalance> => {
  const isIreland = payrollCountry === IRELAND;
  return [
    {
      key: 'systemId',
      title: messages['common.employeeId'],
      dataIndex: 'subcontractor',
      width: 160,
      render: (subcontractor) =>
        subcontractor?.id || subcontractor?.systemId || '_',
    },
    {
      title: isBureau ? messages['table.employerRef'] : messages['common.agencyRef'],

      dataIndex: 'subcontractor',
      width: 240,
      render: (subcontractor) =>
        isBureau
          ? subcontractor?.agencyRef || '_'
          : `${subcontractor?.departmentPrefix || subcontractor?.primaryAgency?.agencyPrefix || '___'}/${
              subcontractor?.agencyRef || '_'
            }`,
    },
    {
      title: messages['common.employeeName'],

      dataIndex: 'subcontractor',
      width: 240,
      render: (subcontractor) =>
        subcontractor?.fullName ||
        `${subcontractor?.forename || ''} ${subcontractor?.surname || ''}` ||
        '_',
    },
    ...(!isBureau ? [{
      title: messages['common.agencyName'],

      dataIndex: 'payroll',
      width: 240,
      render: (payroll: any) => payroll?.agencyRef?.name || '_',
    }] : []),
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      align: 'right' as const,
      dataIndex: 'payroll',

      width: 150,
      render: (payroll: any) => {
        if (!payroll) return '_';
        return `${payroll?.taxPeriod || ''}/${
          payroll?.taxYear || payroll?.selfBillRef?.taxYear || ''
        }`;
      },
    },
    {
      title: messages['common.payFrequency'],

      dataIndex: 'payroll',
      width: 240,
      render: (payroll: any, record: IHolidayPayBalance) => {
        const freq = payroll?.payFrequency || record.subcontractor?.payFrequency;
        return freq
          ? find(defaultPaymentFrequency, (item) => item?.value === freq)?.label
          : '_';
      },
    },
    {
      title: isBureau ? messages['common.employer'] : messages['common.paymentCompany'],
      dataIndex: 'payroll',

      width: 350,
      render: (payroll: any, record: IHolidayPayBalance) => (
        <span>
          {payroll?.companyRef?.companyName
            || record.subcontractor?.companyPayment?.companyName
            || '_'}
        </span>
      ),
    },
    ...(isBureau
      ? [
          {
            title: messages['common.openingBalance'],
            align: 'right' as const,
            dataIndex: 'openingBalance',
            key: 'openingBalance',
            width: 200,
            render: (value: number) =>
              formatMoney(value, showCurrencyByCountry(payrollCountry)),
          },
          {
            title: messages['common.accrued'],
            align: 'right' as const,
            dataIndex: 'accrued',
            key: 'accrued',
            width: 200,
            render: (value: number) =>
              formatMoney(value, showCurrencyByCountry(payrollCountry)),
          },
          {
            title: messages['common.paid'],
            align: 'right' as const,
            dataIndex: 'paid',
            key: 'paid',
            width: 200,
            render: (value: number) =>
              formatMoney(value, showCurrencyByCountry(payrollCountry)),
          },
          {
            title: messages['common.closingBalance'],
            align: 'right' as const,
            dataIndex: 'balance',
            key: 'balance',
            width: 200,
            render: (value: number) =>
              formatMoney(value, showCurrencyByCountry(payrollCountry)),
          },
        ] as ColumnsType<IHolidayPayBalance>
      : [
    {
      title: messages['common.openingBalance'],

      children: [
        {
          title: messages['common.holidayPay'],
          align: 'right' as const,
          dataIndex: 'openingBalance',
          key: 'openingBalance',
          width: 240,
    
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        },
        ...(!isIreland
          ? [
              {
                title: messages['common.ERNI'],
                align: 'right' as const,
                dataIndex: 'openingERNI',
                key: 'openingERNI',
                width: 240,
                render: (value: number) =>
                  formatMoney(value, showCurrencyByCountry(payrollCountry)),
              },
            ]
          : []),
        ...(!isIreland ? [{
          title: messages['common.appLevy'],
          align: 'right' as const,
          dataIndex: 'openingAppLevy',
          key: 'openingAppLevy',
          width: 240,
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        },
        {
          title: messages['common.emprPension'],
          align: 'right' as const,
          dataIndex: 'openingERPension',
          key: 'openingERPension',
          width: 240,
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        }] : []),
      ],
    },
    {
      title: messages['common.accrued'],

      children: [
        {
          title: messages['common.holidayPay'],
          align: 'right' as const,
          dataIndex: 'accrued',
          key: 'accrued',
          width: 240,
    
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        },
        ...(!isIreland
          ? [
              {
                title: messages['common.ERNI'],
                align: 'right' as const,
                dataIndex: 'payroll',
                key: 'ERNI',
                width: 240,
                render: (payroll: any) => {
                  if (!payroll) return '_';
                  const { payeeUmbrellaValue } = payroll;
                  return formatMoneyExport(
                    payeeUmbrellaValue?.retainedHolidayPayERNI,
                    showCurrencyByCountry(payrollCountry),
                  );
                },
              },
            ]
          : []),
        ...(!isIreland ? [{
          title: messages['common.appLevy'],
          align: 'right' as const,
          dataIndex: 'payroll',
          key: 'appLevy',
          width: 240,
          render: (payroll: any) => {
            if (!payroll) return '_';
            const { payeeUmbrellaValue } = payroll;
            return formatMoneyExport(
              payeeUmbrellaValue?.retainedHolidayPayAppLevy,
              showCurrencyByCountry(payrollCountry),
            );
          },
        },
        {
          title: messages['common.emprPension'],
          align: 'right' as const,
          dataIndex: 'payroll',
          key: 'ERPension',
          width: 240,
          render: (payroll: any) => {
            if (!payroll) return '_';
            const { payeeUmbrellaValue } = payroll;
            return formatMoneyExport(
              payeeUmbrellaValue?.retainedHolidayPayErPen,
              showCurrencyByCountry(payrollCountry),
            );
          },
        }] : []),
      ],
    },
    {
      title: messages['common.paid'],

      children: [
        {
          title: messages['common.holidayPay'],
          align: 'right' as const,
          dataIndex: 'paid',
          key: 'paid',
          width: 240,
    
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        },
        ...(!isIreland
          ? [
              {
                title: messages['common.ERNI'],
                align: 'right' as const,
                dataIndex: 'payroll',
                key: 'ERNI',
                width: 240,
                render: (payroll: any) => {
                  if (!payroll) return '_';
                  const { payeeUmbrellaValue } = payroll;
                  return formatMoneyExport(
                    payeeUmbrellaValue?.retainedHPPaidOutERNI,
                    showCurrencyByCountry(payrollCountry),
                  );
                },
              },
            ]
          : []),
        ...(!isIreland ? [{
          title: messages['common.appLevy'],
          align: 'right' as const,
          dataIndex: 'payroll',
          key: 'appLevy',
          width: 240,
          render: (payroll: any) => {
            if (!payroll) return '_';
            const { payeeUmbrellaValue } = payroll;
            return formatMoneyExport(
              payeeUmbrellaValue?.hpRetainedPaidOutAppLevy,
              showCurrencyByCountry(payrollCountry),
            );
          },
        },
        {
          title: messages['common.emprPension'],
          align: 'right' as const,
          dataIndex: 'payroll',
          key: 'ERPension',
          width: 240,
          render: (payroll: any) => {
            if (!payroll) return '_';
            const { payeeUmbrellaValue } = payroll;
            return formatMoneyExport(
              payeeUmbrellaValue?.hpRetainedPaidOutErPen,
              showCurrencyByCountry(payrollCountry),
            );
          },
        }] : []),
      ],
    },
    {
      title: messages['common.closingBalance'],

      children: [
        {
          title: messages['common.holidayPay'],
          align: 'right' as const,
          dataIndex: 'balance',
          key: 'balance',
          width: 240,
    
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        },
        ...(!isIreland
          ? [
              {
                title: messages['common.ERNI'],
                align: 'right' as const,
                dataIndex: 'ERNI',
                key: 'ERNI',
                width: 240,
                render: (value: number) =>
                  formatMoney(value, showCurrencyByCountry(payrollCountry)),
              },
            ]
          : []),
        ...(!isIreland ? [{
          title: messages['common.appLevy'],
          align: 'right' as const,
          dataIndex: 'appLevy',
          key: 'appLevy',
          width: 240,
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        },
        {
          title: messages['common.emprPension'],
          align: 'right' as const,
          dataIndex: 'ERPension',
          key: 'ERPension',
          width: 240,
          render: (value: number) =>
            formatMoney(value, showCurrencyByCountry(payrollCountry)),
        }] : []),
      ],
    },
        ] as ColumnsType<IHolidayPayBalance>),
  ];
};

export const getHolidayPayBalanceSummaryColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<IHolidayPayBalanceSummary> => {
  const isIreland = payrollCountry === IRELAND;
  return [
    {
      key: 'id',
      title: messages['common.employeeId'],
      dataIndex: 'id',

      width: 120,
    },
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['common.agencyRef'],

      width: 240,
      render: (record) =>
        isBureau
          ? record?.agencyRef || record?.systemId || '_'
          : `${record?.primaryAgency?.agencyPrefix || '___'}/${
              record?.agencyRef || record?.systemId || '_'
            }`,
    },
    {
      key: 'name',
      title: messages['common.name'],

      width: 240,
      render: (_value, record) => formatFullName(record) || '_',
    },
    {
      key: 'companyName',
      title: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      dataIndex: 'companyPayment',

      width: 240,
      render: (com) => com?.companyName,
    },
    ...(!isBureau && !isIreland
      ? [
          {
            key: 'agencyName',
            title: messages['common.primaryAgency'],
            dataIndex: 'primaryAgency',

            width: 240,
            render: (a: any) => a?.name || '_',
          },
        ]
      : []),
    {
      key: 'balance',
      title: messages['common.holidayPayBalance'],
      align: 'right',
      dataIndex: 'latestHolidayPay',

      width: 150,
      // NX-5114: use BE-supplied `balance` (rounded via HolidayPay.afterLoad,
      // NX-4390) instead of recomputing raw float4. BE manually invokes
      // afterLoad on the joined entity since TypeORM skips it on joins;
      // see report.payroll.service.ts after getManyAndCount.
      render: (val) =>
        formatMoney(val?.balance ?? 0, showCurrencyByCountry(payrollCountry)),
    },
    ...(isBureau
      ? ([
          {
            key: 'openingBalance',
            title: messages['common.openingBalance'],
            align: 'right' as const,
            dataIndex: 'latestHolidayPay',
            width: 150,
            render: (val: any) =>
              formatMoney(
                val?.openingBalance,
                showCurrencyByCountry(payrollCountry),
              ),
          },
          {
            key: 'accrued',
            title: messages['common.accrued'],
            align: 'right' as const,
            dataIndex: 'latestHolidayPay',
            width: 150,
            render: (val: any) =>
              formatMoney(val?.accrued, showCurrencyByCountry(payrollCountry)),
          },
          {
            key: 'paid',
            title: messages['common.paid'],
            align: 'right' as const,
            dataIndex: 'latestHolidayPay',
            width: 150,
            render: (val: any) =>
              formatMoney(val?.paid, showCurrencyByCountry(payrollCountry)),
          },
        ] as ColumnsType<IHolidayPayBalanceSummary>)
      : !isIreland
      ? ([
          {
            key: 'ERNI',
            title: messages['common.ERNI'],
            align: 'right' as const,
            dataIndex: 'latestHolidayPay',
            width: 150,
            render: (val: any) =>
              formatMoney(val?.ERNI, showCurrencyByCountry(payrollCountry)),
          },
          {
            key: 'appLevy',
            title: messages['common.appLevy'],
            align: 'right' as const,
            dataIndex: 'latestHolidayPay',

            width: 150,
            render: (val: any) =>
              formatMoney(val?.appLevy, showCurrencyByCountry(payrollCountry)),
          },
          {
            key: 'emprPension',
            title: messages['common.emprPension'],
            align: 'right' as const,
            dataIndex: 'latestHolidayPay',

            render: (val: any) =>
              formatMoney(
                val?.ERPension,
                showCurrencyByCountry(payrollCountry),
              ),
            width: 150,
          },
        ] as ColumnsType<IHolidayPayBalanceSummary>)
      : []),
  ];
};

export const getNetPayColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<INetPay> => {
  return [
    {
      key: 'id',
      title: messages['common.employeeId'],
      dataIndex: 'payroll',

      render: (payroll) => {
        const id = payroll?.subcontractorRef?.id;
        return id || '_';
      },
    },
    {
      key: 'agencyRef',
      title: isBureau ? messages['table.employerRef'] : messages['common.agencyRef'],
      dataIndex: 'payroll',

      width: 250,
      render: (payroll) => {
        const agencyPrefix = payroll?.subcontractorRef?.departmentPrefix || payroll?.agencyRef?.agencyPrefix || '___';
        const agencyRef = payroll?.subcontractorRef?.agencyRef || '';
        const employeeId = payroll?.subcontractorRef?.id || '';
        return `${agencyPrefix}/${agencyRef || employeeId || '_'}`;
      },
    },
    {
      key: 'name',
      title: messages['common.name'],
      dataIndex: 'payroll',

      render: (payroll) => formatFullName(payroll?.subcontractorRef) || '_',
    },

    {
      key: 'taxPeriod',
      title: messages['table.taxPeriod'],
      align: 'right',

      dataIndex: 'payroll',
      render: (payroll) => {
        const taxPeriod = payroll?.taxPeriod;
        const taxYear = payroll?.taxYear;
        return `${taxPeriod}/${taxYear}`;
      },
    },
    {
      key: 'paymentModel',
      title: messages['common.paymentModel'],

      dataIndex: 'payroll',
      render: (payroll) =>
        paymentModels[payroll?.paymentModel as keyof typeof paymentModels] ||
        '_',
    },
    {
      key: 'payFrequency',
      title: messages['common.payFrequency'],

      dataIndex: 'payroll',
      render: (payroll) =>
        payroll
          ? find(
              defaultPaymentFrequency,
              (item) => item?.value === payroll?.payFrequency,
            )?.label
          : '_',
    },
    {
      key: 'companyId',
      title: messages['common.companyId'],
      dataIndex: 'payroll',

      render: (payroll) => {
        const companyId = payroll?.companyRef?.id;
        return companyId || '_';
      },
    },
    {
      key: 'companyName',
      title: isBureau ? messages['common.employer'] : messages['common.paymentCompany'],
      dataIndex: 'payroll',

      width: 350,
      render: (payroll) => {
        const companyName = payroll?.companyRef?.companyName;
        return companyName || '_';
      },
    },
    ...(!isBureau ? [{
      key: 'agencyName',
      title: messages['common.agencyName'],
      dataIndex: 'payroll',

      width: 350,
      render: (payroll: any) => {
        const agencyName = payroll?.agencyRef?.name;
        return agencyName || '_';
      },
    }] : []),

    {
      key: 'timesheetValue',
      title: messages['common.timesheetValue'],
      align: 'right' as const,
      dataIndex: 'timesheetValue',

      render: (timesheetValue: any) => {
        return formatMoney(
          timesheetValue,
          showCurrencyByCountry(payrollCountry),
        );
      },
    },
    {
      key: 'netPay',
      title: messages['common.netPay'],
      align: 'right',
      dataIndex: 'netPay',

      render: (netPay) =>
        formatMoney(netPay, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'net',
      title: messages['common.totalNet'],
      align: 'right',
      dataIndex: 'payroll',

      render: (payroll) => {
        const net = payroll?.net;
        return formatMoney(net, showCurrencyByCountry(payrollCountry));
      },
    },
    {
      key: 'paid',
      title: messages['common.paid'],
      align: 'right',
      dataIndex: 'paid',

      render: (paid) =>
        formatMoney(paid, showCurrencyByCountry(payrollCountry)),
    },
    {
      key: 'paymentDate',
      title: messages['common.paymentDate'],
      align: 'right',
      dataIndex: 'payroll',

      render: (payroll) =>
        payroll?.paymentDate
          ? moment(payroll?.paymentDate)
              .tz('Europe/London')
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'selfBillRef',
      title: messages['common.payslipId'],
      dataIndex: 'payroll',

      render: (payroll) => {
        const selfBillRef = payroll?.selfBillRef?.id;
        const paymentModel = payroll?.paymentModel;
        if (!selfBillRef) return '_';
        if (['CIS', 'NON_CIS'].includes(paymentModel)) {
          return selfBillRef;
        } else return selfBillRef?.replace('SFB', 'PAY');
      },
    },
    {
      key: 'invoiceNo',
      title: messages['common.salesInvRef'],

      dataIndex: 'timesheets',
      render: (timesheets) => {
        return !timesheets?.length
          ? '_'
          : Array.from(new Set(timesheets.map((e: any) => e.invoiceNo))).join(
              ', ',
            );
      },
    },
  ];
};

export const getFiftyTwoWeekAverageSummaryColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<IFiftyTwoWeekAverageSummary> => {
  return [
    {
      key: 'agencyRef',
      title: isBureau ? messages['table.employerRef'] : messages['common.agencyRef'],
      dataIndex: 'subcontractor',

      width: 250,
      render: (subcontractor) =>
        `${subcontractor?.departmentPrefix || subcontractor?.primaryAgency?.agencyPrefix || '___'}/${
          subcontractor?.agencyRef || subcontractor?.systemId || '_'
        }`,
    },
    {
      key: 'name',
      title: messages['common.name'],
      dataIndex: 'subcontractor',

      render: (subcontractorRef) => formatFullName(subcontractorRef) || '_',
    },
    {
      key: 'status',
      title: messages['common.status'],
      align: 'center',
      dataIndex: 'subcontractor',

      render: (subcontractorRef) => {
        const status = subcontractorRef?.status;
        return formatStatus(status) || '_';
      },
    },
    {
      key: 'companyPayment',
      title: isBureau ? messages['common.employer'] : messages['common.paymentCompany'],

      dataIndex: 'subcontractor',
      render: (subcontractorRef) =>
        subcontractorRef?.companyPayment?.companyName || '_',
    },
    ...(!isBureau ? [{
      key: 'agency',
      title: messages['common.agencyName'],

      dataIndex: 'subcontractor',
      render: (subcontractorRef: any) =>
        subcontractorRef?.primaryAgency?.name || '_',
    }] : []),
    {
      key: 'earliestPayrollDate',
      title: messages['common.holidayYearStart'],
      align: 'right',
      dataIndex: 'earliestPayrollDate',

      render: (earliestPayrollDate) =>
        earliestPayrollDate
          ? moment(earliestPayrollDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'accruedHolidayHours',
      title: messages['common.accruedHolidayHours'],

      render: (summary) =>
        (
          Number(summary?.averageHours || 0) *
          Number(summary?.totalWeeksUsed || 0) *
          0.1207
        ).toFixed(2),
    },
    {
      key: 'averageHours',
      title: messages['common.paidHolidayHours'],
      align: 'right',
      dataIndex: 'paidHolidayHours',

      render: (paidHolidayHours) => Number(paidHolidayHours || 0).toFixed(2),
    },
    {
      key: 'remainingHolidayHours',
      title: messages['common.remainingHolidayHours'],

      render: (summary) => {
        const accruedHolidayHours =
          Number(summary?.averageHours || 0) *
          Number(summary?.totalWeeksUsed || 0) *
          0.1207;
        const paidHolidayHours = Number(summary?.paidHolidayHours || 0);
        return Math.max(0, accruedHolidayHours - paidHolidayHours).toFixed(2);
      },
    },
    {
      key: 'averageHourlyRate',
      title: messages['common.fiftyTwoWeeksHoursWorked'],

      render: (summary) => {
        const totalWeeksUsed =
          Number(summary?.totalWeeksUsed || 0) *
          Number(summary?.averageHours || 0);
        return totalWeeksUsed.toFixed(2);
      },
    },
    {
      key: 'fiftyTwoWeeksHoursWorked',
      title: messages['common.fiftyTwoWeeksWeeksWorked'],
      align: 'right',
      dataIndex: 'totalWeeksUsed',

      render: (totalWeeksUsed) => parseInt(totalWeeksUsed),
    },
    {
      key: 'totalGross',
      title: messages['common.fiftyTwoWeeksTotalGross'],
      align: 'right',

      render: (summary) => {
        const totalGross =
          Number(summary?.averageGross || 0) *
          Number(summary?.totalWeeksUsed || 0);
        return formatMoney(
          totalGross || 0,
          showCurrencyByCountry(payrollCountry),
        );
      },
    },
    {
      key: 'averageGross',
      title: messages['common.fiftyTwoWeeksAverageGross'],
      align: 'right',
      dataIndex: 'averageGross',

      render: (averageGross) =>
        formatMoney(averageGross, showCurrencyByCountry(payrollCountry)),
    },
  ];
};

export const getP30ReportColumns = ({
  messages,
  handleViewDetail,
  payrollCountry,
  isBureau,
}: {
  messages: TypedMessages;
  isIreland: boolean;
  handleViewDetail: (record: P30P32RecordType) => void;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
}): ColumnsType<P30P32RecordType> => {
  return [
    {
      key: 'action',
      title: messages['common.action'],
      align: 'left',
      fixed: 'left',

      render: (record) => (
        <ul className="table-actions flex items-center justify-center">
          <li onClick={() => handleViewDetail(record)}>
            <Tooltip title="View">
              <span><Icons.IconEye /></span>
            </Tooltip>
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'companyName',
      title: isBureau ? messages['common.employer'] : messages['common.companyName'],

      render: (record) => record.companyRef?.companyName,
      width: 200,
    },
    {
      key: 'taxMonth',
      title: messages['common.taxMonth'],
      align: 'right',

      render: (record) => record.taxMonth,
      width: 160,
    },
    {
      key: 'tax',
      title: messages['report.hmrc.grossTax'],
      align: 'right',

      render: (record) => formatMoney(record.tax || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      key: 'receivedFromHMRCToRefundTax',
      title: messages['report.hmrc.receivedFromHMRCToRefundTax'],
      align: 'right',

      render: (record) => formatMoney(record.receivedFromHMRCToRefundTax || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      key: 'totalCisDeduction',
      title: messages['report.hmrc.grossStudentAndPostgraduateLoansDeductions'],
      align: 'right',

      render: (record) => formatMoney(record.totalCisDeduction || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      key: 'cisDeductionsSuffered',
      title: messages['report.hmrc.cisDeductionsSuffered'],
      align: 'right',

      render: (record) => formatMoney(record.cisDeductionsSuffered || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      key: 'grossStudentLoanDeductions',
      title: messages['report.hmrc.grossStudentLoanDeductions'],
      align: 'right',

      render: (record) => formatMoney(record.grossStudentLoanDeductions || 0, showCurrencyByCountry(payrollCountry)),
      width: 200,
    },
    {
      key: 'grossPostgraduateLoanDeductions',
      title: messages['report.hmrc.grossPostgraduateLoanDeductions'],
      align: 'right',
      render: (record) => formatMoney(record.grossPostgraduateLoanDeductions || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'netIncomeTax',
      title: messages['report.hmrc.netIncomeTax'],
      align: 'right',
      render: (record) =>
        formatMoney(
          (record?.tax || 0) +
          (parseFloat(record?.receivedFromHMRCToRefundTax) || 0) -
          (parseFloat(record?.cisDeductionsSuffered) || 0) +
          (record?.totalCisDeduction || 0) +
          (record?.grossStudentLoanDeductions || 0) +
          (record?.grossPostgraduateLoanDeductions || 0),
          showCurrencyByCountry(payrollCountry),
        ),

      width: 200,
    },
    {
      key: 'grossEmployeeNI',
      title: messages['report.hmrc.grossEmployeeNI'],
      align: 'right',
      render: (record) => formatMoney(record.grossEmployeeNI || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'grossEmployersNI',
      title: messages['report.hmrc.grossEmployersNI'],
      align: 'right',
      render: (record) => formatMoney(record.grossEmployersNI || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'totalStatutoryPayRecovered',
      title: messages['report.hmrc.totalStatutoryPayRecovered'],
      align: 'right',
      render: (record) => formatMoney(record.totalStatutoryPayRecovered || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'totalNICCompensation',
      title: messages['report.hmrc.totalNICCompensation'],
      align: 'right',
      render: (record) => formatMoney(record.totalNICCompensation || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'receivedFromHMRCToPayStatutoryPay',
      title: messages['report.hmrc.receivedFromHMRCToPayStatutoryPay'],
      align: 'right',
      render: (record) => formatMoney(record.receivedFromHMRCToPayStatutoryPay || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'empAllowanceClaimed',
      title: messages['report.hmrc.empAllowanceClaimed'],
      align: 'right',
      render: (record) => formatMoney(record.empAllowanceClaimed || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'appLevyPayable',
      title: messages['report.hmrc.appLevyPayable'],
      align: 'right',
      render: (record) => formatMoney(record.appLevyPayable || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'netNics',
      title: messages['report.hmrc.netNIContributions'],
      align: 'right',
      render: (record) =>
        formatMoney(record?.netNIContributions || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'previousTaxMonthsDebtYtd',
      title: messages['report.hmrc.previousTaxMonthsDebtYtd'],
      align: 'right',
      render: (record) => formatMoney(record.previousTaxMonthsDebtYtd || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'previousTaxMonthsPaidYtd',
      title: messages['report.hmrc.previousTaxMonthsPaidYtd'],
      align: 'right',
      render: (record) => formatMoney(record.previousTaxMonthsPaidYtd || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'previousTaxMonthsShortFall',
      title: messages['report.hmrc.previousTaxMonthsShortFall'],
      align: 'right',
      render: (record) => formatMoney(record.previousTaxMonthsShortFall || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
    {
      key: 'adjustment',
      title: messages['report.hmrc.adjustment'],
      align: 'right',
      render: (record) => formatMoney(record.adjustment || 0, showCurrencyByCountry(payrollCountry)),

      width: 200,
    },
  ];
};
